import { useNavigate } from "react-router-dom";
import { ComponentPropsWithoutRef, useCallback, useMemo } from "react";
import { isMatrixCommandStep, isParallelCommandStep, Outcome, State } from "app/lib/pipeline";
import Icon from "app/components/shared/Icon";
import Indicator from "app/components/shared/Indicator";
import { CommandJob } from "app/components/build/Show/lib/types";
import { useTrack } from "app/components/build/Show/lib/useTrack";

import { ControlButton } from "./ControlButton";
import { useHotKey } from "./useHotKey";
import {
  findJobsForStep,
  mostRecentMatrixJobs,
  mostRecentParallelJobs,
  useBuild,
} from "app/components/build/Show/lib/BuildContext";
import useQueryParams from "app/components/build/Show/lib/useQueryParams";
import { resolveJobUrl, resolveStepUrl } from "app/components/build/Show/lib/urlForView";

/**
 * Get the list of current failing steps (and jobs) in the build path.
 *
 * @returns
 */
export const useFailurePaths = (): { step: string; job?: string }[] => {
  const { build } = useBuild();

  return useMemo(() => {
    return (
      build.steps
        .filter(
          (step) =>
            step.type !== "group" &&
            (step.state === State.Failing ||
              (step.state === State.Finished &&
                (step.outcome === Outcome.HardFailed || step.outcome === Outcome.SoftFailed))),
        )
        .flatMap((step) => {
          // Non-sidebar canvas only supports jumping to steps rather than jobs.
          if (!Features.BuildSidebar) {
            return { step: step.uuid };
          }

          // Find the latest parallel failed jobs for the step.
          if (isParallelCommandStep(step)) {
            const jobs = findJobsForStep<CommandJob>(build, step.uuid).filter(
              (job) => job.state === "finished" && !job.passed,
            );
            return mostRecentParallelJobs(jobs).map((job) => ({
              job: job.id,
              step: step.uuid,
            }));
          }

          // Find the latest matrix failed jobs for the step.
          if (isMatrixCommandStep(step)) {
            const jobs = findJobsForStep<CommandJob>(build, step.uuid).filter(
              (job) => job.state === "finished" && !job.passed,
            );
            return mostRecentMatrixJobs(jobs).map((job) => ({
              job: job.id,
              step: step.uuid,
            }));
          }

          return { step: step.uuid };
        }) || []
    );
  }, [build.steps, build?.jobs]);
};

export const GoToFailure = (props: Partial<ComponentPropsWithoutRef<typeof ControlButton>>) => {
  const failures = useFailurePaths();
  const { stepUuid, jobUuid } = useQueryParams();
  const navigate = useNavigate();
  const track = useTrack();

  const focusOnFailure = useCallback(() => {
    if (!failures.length) {
      return;
    }

    let nextFailure: { step: string; job?: string };

    const current = failures.find((failure) => {
      if (jobUuid) {
        return failure.job === jobUuid;
      }
      if (stepUuid) {
        return failure.step === stepUuid;
      }
      return false;
    });

    if (!current) {
      nextFailure = failures[0];
    } else {
      const idx = failures.indexOf(current);

      if (idx + 1 < failures.length) {
        nextFailure = failures[idx + 1];
      } else {
        nextFailure = failures[0];
      }
    }

    track("Click Go To Failure");

    // Navigate to the step or job based on the failure.
    if (nextFailure.job) {
      navigate(resolveJobUrl(nextFailure.job), { state: { forceFocus: true } });
    } else {
      navigate(resolveStepUrl(nextFailure.step), { state: { forceFocus: true } });
    }
  }, [failures, stepUuid, jobUuid]);

  useHotKey("f", focusOnFailure);

  return (
    <ControlButton
      {...props}
      onClick={focusOnFailure}
      label="Go to failure (F)"
      disabled={!failures.length}
    >
      {failures.length > 0 && (
        <span className="absolute left-full bottom-full -translate-x-1/2 translate-y-1/2">
          <Indicator />
        </span>
      )}
      <Icon icon="custom/outline/redo-dot" className="h-[18px] w-5" />
    </ControlButton>
  );
};
